import React, { memo, useEffect, useState } from 'react';

import { useDebounce } from 'react-use';

import { Slider, withStyles } from '@material-ui/core';

/**
 * Dual-range threshold slider — two thumbs on one track for setting the
 * Low/High boundaries at once (variant A). Styled to match the project's
 * PrettoSlider (RangeSliderInput), but the track between the thumbs is
 * recolored per-segment via the wrapper so Low / Medium / High read as
 * distinct zones.
 *
 * The colored segments are drawn by an absolutely-positioned strip BEHIND the
 * MUI rail (the rail itself is made transparent), because a single MUI track
 * can't be multi-colored. Thumbs + interaction come from MUI.
 */
const PrettoRangeSlider = withStyles({
  root: {
    color: '#233841',
    height: 6,
    padding: '13px 0',
  },
  thumb: {
    height: 12,
    width: 12,
    backgroundColor: '#fff',
    marginTop: 0,
    marginLeft: -6,
    '&$active': {
      boxShadow: '0 0 0 6px rgba(35, 56, 65, 0.16) !important',
    },
    '&:focus, &:hover': {
      boxShadow: '0 0 0 4px rgba(35, 56, 65, 0.16)',
    },
    boxShadow: '0 0 0 4px rgba(35, 56, 65, 0.12)',
  },
  active: {},
  valueLabel: {
    top: -30,
    left: 'calc(-50% - 12px)',
    background: '#233841',
    padding: '0px 8px',
    borderRadius: 12,
    '& *': {
      background: 'transparent',
      color: 'white',
    },
  },
  track: {
    height: 6,
    borderRadius: 4,
    // The track is the segment between the two thumbs — we don't color it
    // here (the segmented strip behind handles all colors), so keep it clear.
    backgroundColor: 'transparent',
  },
  rail: {
    height: 6,
    borderRadius: 4,
    backgroundColor: 'transparent',
    opacity: 1,
  },
})(Slider);

interface ThresholdRangeSliderProps {
  /** Current [low, mid] thresholds. */
  value: [number, number];
  min: number;
  max: number;
  step?: number;
  /** Colors for the three severity zones. */
  lowColor: string;
  midColor: string;
  highColor: string;
  /** Called (debounced) with the clamped [low, mid] pair. */
  onChange: (value: [number, number]) => void;
}

function ThresholdRangeSliderBase({
  value,
  min,
  max,
  step = 1,
  lowColor,
  midColor,
  highColor,
  onChange,
}: ThresholdRangeSliderProps) {
  const [internal, setInternal] = useState<[number, number]>(value);

  useDebounce(
    () => {
      if (value[0] !== internal[0] || value[1] !== internal[1]) onChange(internal);
    },
    300,
    [internal],
  );

  useEffect(() => {
    if (value[0] !== internal[0] || value[1] !== internal[1]) setInternal(value);
  }, [value[0], value[1]]);

  const [low, mid] = internal;
  const span = max - min || 1;
  const lowPct = ((low - min) / span) * 100;
  const midPct = ((mid - min) / span) * 100;

  return (
    <div className="w-full">
      <div className="flex items-center gap-4 bg-v2blueGray-0 px-4 h-[44px] rounded-lg">
        <div className="relative flex-1">
          {/* Segmented color strip behind the rail */}
          <div
            className="absolute left-0 right-0 flex h-[6px] rounded overflow-hidden pointer-events-none"
            style={{ top: '50%', transform: 'translateY(-50%)' }}
          >
            <div style={{ width: `${lowPct}%`, background: lowColor }} />
            <div style={{ width: `${midPct - lowPct}%`, background: midColor }} />
            <div style={{ flex: 1, background: highColor }} />
          </div>

          <PrettoRangeSlider
            value={internal}
            min={min}
            max={max}
            step={step}
            valueLabelDisplay="auto"
            onChange={(_e, v) => {
              if (Array.isArray(v) && v.length === 2) {
                // Keep low strictly below mid
                let [a, b] = v as number[];
                if (a >= b) {
                  // Whichever thumb was dragged past the other gets clamped
                  if (a === internal[0]) b = a + 1;
                  else a = b - 1;
                }
                setInternal([a, b]);
              }
            }}
            aria-labelledby="threshold-range-slider"
          />
        </div>
      </div>

      {/* Min / max bounds under the track. The live low/high values show on
          the thumbs themselves via MUI's valueLabel (appears on hover/drag). */}
      <div className="flex justify-between mt-1 px-4">
        <span className="text-[10px] text-v2blueGray-500">{min}</span>
        <span className="text-[10px] text-v2blueGray-500">{max}</span>
      </div>
    </div>
  );
}

export const ThresholdRangeSlider = memo(ThresholdRangeSliderBase);
export default ThresholdRangeSlider;
